import type { QueryClient } from "@tanstack/react-query";

import type { MrpForecastArrivalKind, MrpForecastResult, MrpForecastSourcingCpnSummary } from "@/lib/mrp";
import { applyMrpForecastReserveChange } from "@/lib/mrpForecastRollForward";
import { MRP_FORECAST_SOURCING_QUERY_ROOT } from "@/lib/mrpForecastSourcingQuery";
import { applyForecastArrivalDelta } from "@integrator-src/mrp/forecast-series-reroll";

/** Update batch sourcing cache after the vendors callout saves. */
export function patchMrpForecastSourcingInCache(
  queryClient: QueryClient,
  cpn: string,
  summary: MrpForecastSourcingCpnSummary,
): void {
  queryClient.setQueriesData<Record<string, MrpForecastSourcingCpnSummary>>(
    { queryKey: MRP_FORECAST_SOURCING_QUERY_ROOT },
    (previous) => {
      if (previous == null) return previous;
      return {
        ...previous,
        [cpn]: summary,
      };
    },
  );
}

/**
 * Recompute one CPN × location forecast row in the React Query cache after a reserve edit.
 * Returns false when that row is not present (caller may fall back to a full refetch).
 */
export function patchMrpForecastReserveInCache(
  queryClient: QueryClient,
  cpn: string,
  locationCode: string,
  newAllocatedReserve: number,
): boolean {
  let patched = false;
  queryClient.setQueriesData<MrpForecastResult>({ queryKey: ["mrp", "forecast"] }, (previous) => {
    if (previous == null) return previous;
    const result = applyMrpForecastReserveChange(
      previous.series,
      cpn,
      locationCode,
      newAllocatedReserve,
    );
    if (!result.patched) return previous;
    patched = true;
    return {
      ...previous,
      series: result.series,
    };
  });
  return patched;
}

/**
 * Recompute one CPN x location forecast row after an incoming arrival change.
 * Returns false when that row is not present (caller may fall back to a full refetch).
 */
export function patchMrpForecastArrivalInCache(
  queryClient: QueryClient,
  args: {
    cpn: string;
    locationCode: string;
    monthKey: string;
    quantityDelta: number;
    kind: MrpForecastArrivalKind;
  },
): boolean {
  let patched = false;
  queryClient.setQueriesData<MrpForecastResult>({ queryKey: ["mrp", "forecast"] }, (previous) => {
    if (previous == null) return previous;
    const result = applyForecastArrivalDelta(previous.series, args);
    if (!result.patched) return previous;
    patched = true;
    return {
      ...previous,
      series: result.series,
    };
  });
  return patched;
}

/** Patch open transfer-request demand (origin negative, destination positive) in cached forecast rows. */
export function patchMrpForecastTransferRequestInCache(
  queryClient: QueryClient,
  args: {
    cpn: string;
    originLocationCode: string | null;
    destinationLocationCode: string | null;
    targetDate: string;
    remainingQuantity: number;
    sign: 1 | -1;
  },
): boolean {
  const remaining = args.remainingQuantity;
  if (!Number.isFinite(remaining) || remaining <= 0) {
    return true;
  }
  const trimmedCpn = args.cpn.trim();
  if (trimmedCpn === "") {
    return true;
  }
  let patched = true;
  if (args.originLocationCode != null && args.originLocationCode.trim() !== "") {
    patched =
      patchMrpForecastArrivalInCache(queryClient, {
        cpn: trimmedCpn,
        locationCode: args.originLocationCode.trim(),
        monthKey: args.targetDate,
        quantityDelta: args.sign * -remaining,
        kind: "transfer",
      }) && patched;
  }
  if (args.destinationLocationCode != null && args.destinationLocationCode.trim() !== "") {
    patched =
      patchMrpForecastArrivalInCache(queryClient, {
        cpn: trimmedCpn,
        locationCode: args.destinationLocationCode.trim(),
        monthKey: args.targetDate,
        quantityDelta: args.sign * remaining,
        kind: "transfer",
      }) && patched;
  }
  return patched;
}
